import {gql} from '@apollo/client';
import {useMemo, useRef} from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {getCommonStudyType} from 'studies/typed/utils/analysis/analysisUtils';

import {SeedItemsSelectedStudyTypesQuery} from './__generated/SeedItemsSelectedStudyTypesQuery';
import {SeedItemsStudySchemaTypesQuery} from './__generated/SeedItemsStudySchemaTypesQuery';

export const SEED_ITEMS_STUDY_SCHEMA_TYPES_QUERY = gql`
  query SeedItemsStudySchemaTypesQuery @skipAudit @engComponent(name: STUDIES) {
    studySchemas {
      id
      studyType
    }
  }
`;

export const SEED_ITEMS_SELECTED_STUDY_TYPES_QUERY = gql`
  query SeedItemsSelectedStudyTypesQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: STUDIES) {
    studies(ids: $ids) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const ALLOWED_STUDY_TYPES: ReadonlySet<StudyType> = new Set([
  StudyType.ASSAY_DEVELOPMENT,
  StudyType.PROCESS_DEVELOPMENT,
]);

export interface StudySchemaInfo {
  id: string;
  studyType: StudyType;
}

interface UseResolvedStudyTypeResult {
  studyType: StudyType | null;
  isLoading: boolean;
}

export function useAllowedStudySchemas(): Array<StudySchemaInfo> | null {
  const result = useBenchlingQuery<SeedItemsStudySchemaTypesQuery>(SEED_ITEMS_STUDY_SCHEMA_TYPES_QUERY);
  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }
  return result.data.studySchemas.filter((schema) => ALLOWED_STUDY_TYPES.has(schema.studyType));
}

/**
 * Resolves the study type once per selection session and caches the result.
 *
 * Lifecycle:
 *   1. No studies selected - resolves to null
 *   2. First study selected — fires a query to fetch the study type.
 *   3. Query resolves — caches the type and returns it.
 *   4. More studies added — skips the query since the type is already known and we rely on
 *      filtering the search input in StudyPicker to show only studies of the same type.
 *   5. In the case of all studies being cleared, the hook resets the cached study type, returning to step 1.
 *
 * This avoids redundant network requests when adding multiple studies, since type-based schema filtering
 * already ensures subsequent selections share the same type.
 */
export function useResolvedStudyType(selectedIds: Array<string>): UseResolvedStudyTypeResult {
  const resolvedStudyTypeRef = useRef<StudyType | null>(null);

  if (selectedIds.length === 0) {
    resolvedStudyTypeRef.current = null;
  }

  const shouldQuery = selectedIds.length >= 1 && resolvedStudyTypeRef.current == null;

  const result = useSkippableBenchlingQuery<SeedItemsSelectedStudyTypesQuery>(
    SEED_ITEMS_SELECTED_STUDY_TYPES_QUERY,
    {variables: {ids: selectedIds}, skip: !shouldQuery}
  );

  if (result.type === QueryResultType.SUCCESS) {
    const studies = accessibleObjs(result.data.studies);
    const commonStudyType = getCommonStudyType(studies.map((s) => s.studySchema.studyType));
    if (commonStudyType != null) {
      resolvedStudyTypeRef.current = commonStudyType;
    }
  }

  const isLoading = result.type === QueryResultType.PENDING && resolvedStudyTypeRef.current == null;

  return {studyType: resolvedStudyTypeRef.current, isLoading};
}

export function useFilteredStudySchemaIds(
  allSchemas: Array<StudySchemaInfo> | null,
  selectedStudyType: StudyType | null
): Array<string> | null {
  return useMemo(() => {
    if (allSchemas == null) {
      return null;
    }
    if (selectedStudyType == null) {
      return allSchemas.map((s) => s.id);
    }
    return allSchemas.filter((s) => s.studyType === selectedStudyType).map((s) => s.id);
  }, [allSchemas, selectedStudyType]);
}
